import FaqAccordion from '@/components/content/FaqAccordion'

const PRO_TIPS = [
  'Buy from retailers with sale-or-return policies (e.g., Total Wine, Trader Joe\'s) so unopened cases can be returned.',
  'Skip the champagne toast or substitute with Prosecco/Cava for a lower-cost sparkling option.',
  'Stick to 2 signature cocktails instead of running a full-service liquor bar.',
  'Buy bulk beer in kegs vs. craft cans (or vice versa) depending on deposit fees and venue setup.',
  'Calculate non-alcoholic drinks and mixers accurately to avoid emergency venue runs.',
  'Account for non-drinkers (vendors, children, designated drivers) when estimating headcount.',
  'Adjust seasonally: summer weddings lean into white wine, rosé, and light beer; winter weddings favor red wine and dark spirits.',
]

const COST_TABLE = [
  { guests: 50, low: 750, high: 2250 },
  { guests: 100, low: 1500, high: 4500 },
  { guests: 150, low: 2250, high: 6750 },
  { guests: 200, low: 3000, high: 9000 },
  { guests: 250, low: 3750, high: 11250 },
]

const QUANTITY_TABLE = [
  { guests: 50, h3: { wine: 5, beer: 2, spirits: 3 }, h4: { wine: 7, beer: 3, spirits: 4 }, h5: { wine: 9, beer: 4, spirits: 5 } },
  { guests: 100, h3: { wine: 10, beer: 5, spirits: 6 }, h4: { wine: 13, beer: 6, spirits: 8 }, h5: { wine: 17, beer: 8, spirits: 10 } },
  { guests: 150, h3: { wine: 15, beer: 7, spirits: 9 }, h4: { wine: 20, beer: 9, spirits: 12 }, h5: { wine: 25, beer: 12, spirits: 15 } },
  { guests: 200, h3: { wine: 20, beer: 9, spirits: 12 }, h4: { wine: 26, beer: 12, spirits: 16 }, h5: { wine: 33, beer: 15, spirits: 20 } },
]

const BLOG_IDEAS = [
  'How Much Alcohol to Buy for a 100-Guest Wedding Reception',
  'How Many Bottles of Wine Do You Need for 150 Wedding Guests?',
  'Open Bar Cost Calculator: How Much Will You Really Spend?',
  'Beer and Wine Only Wedding Alcohol Guide & Quantity Chart',
  'How Many Drinks Per Person for a 4-Hour Wedding Reception?',
  'The Ultimate Keg Calculator for Weddings: Half vs. Quarter Barrels',
  'How Much Champagne Do You Need for a 100 Person Toast?',
  'How to Buy Your Own Alcohol for a BYOB Wedding Venue',
  'How Much Ice Do You Really Need for a Wedding Bar?',
  'Non-Alcoholic Drink Calculator: Sodas, Mocktails, and Water Ratios',
  'How Much Alcohol to Buy for a 200 Person Wedding',
  'Average Cost of Alcohol Per Person at a Wedding',
  'How to Calculate Mixers for a Wedding Liquor Bar',
  'Signature Cocktails Guide: How Much Batch Alcohol to Prepare',
  'Return Unopened Liquor: State Laws & Retailer Return Policies',
  'Summer vs. Winter Wedding Drink Ratios: What Changes?',
  'How Many Bottles of Vodka, Tequila, and Bourbon for a Full Bar?',
  'Cash Bar vs. Open Bar vs. Consumption Bar: Complete Cost Analysis',
  'How Much Booze Do You Need for a 50-Guest Intimate Wedding?',
  'Rehearsal Dinner Drink Calculator: How Much Alcohol to Serve',
  'Wedding Bartender Guide: How Many Bartenders Do You Need for 150 Guests?',
  'How to Budget for Wedding Alcohol on a $1,000 Limit',
  'Total Wine Wedding Orders: How to Get Bulk Discounts & Delivery',
  'How Many Beers Are in a Half-Barrel Keg? (Wedding Beer Math)',
  'Can You Buy Alcohol at Costco for a Wedding? Top Tips & Savings',
  'How Much Wine Per Person at a Sit-Down Wedding Dinner?',
  'How Much Liquor to Buy for a Backyard Wedding Reception',
  'Soft Drink & Soda Calculator for Children and Non-Drinkers',
  'What Is the Best Ratio for Red, White, and Rosé Wine at Weddings?',
  'How to Calculate Alcohol for a Cocktail Hour Only',
  'How Much Alcohol Do Heavy Drinkers Consume at Weddings?',
  'The Best Affordable Wines for Weddings That Taste Expensive',
  'How to Create a Wedding Bar Shopping List in 5 Easy Steps',
  'How Much Beer to Buy for 100 Guests: Cans vs. Bottles vs. Kegs',
  'How Much Alcohol for a Sunday Afternoon Wedding?',
  'Wedding Alcohol Consumption Patterns: Hour-by-Hour Breakdown',
  'How Much Alcohol for a 250-Guest Luxury Wedding?',
  'Essential Barware and Accessories Checklist for DIY Wedding Bars',
  'How to Avoid Running Out of Alcohol at Your Wedding Reception',
  'How Much Soda, Juice, and Tonic to Buy per Bottle of Spirits',
  'Wine Bottle Calculator for Weddings: How Many Glasses Per Bottle?',
  'How Much Does a Self-Serve Beer Tap Wall Cost for Weddings?',
  'How Much Alcohol Should You Buy for a Destination Wedding?',
  'Alcohol Rules & Insurance Requirements for Self-Catered Wedding Venues',
  'How to Calculate Drinks for a Bachelorette Party & Wedding Weekend',
  'How Much Champagne for 150 Guests: Toast vs. Full Bar',
  'What Spirits Should You Stock for a Standard Wedding Bar?',
  'How to Calculate Alcohol Quantities for a Brunch Wedding',
  'How Much Beer, Wine, and Liquor for a 5-Hour Evening Reception?',
  'Printable Wedding Alcohol Shopping List & Store Checklist',
]

function H2({ children, id }: { children: React.ReactNode, id: string }) {
  return (
    <h2 id={id} className="font-serif text-2xl sm:text-3xl font-bold text-[#6B1D2F] mt-14 mb-4 scroll-mt-24">
      {children}
    </h2>
  )
}

export default function SeoContent() {
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-[#222222]">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
        Wedding Alcohol Calculator: Estimate Your Drink Quantities & Budget
      </h1>
      <p className="mt-5 text-lg leading-relaxed text-gray-700">
        Getting wedding alcohol math wrong is expensive in either direction. Under-order and your bar runs dry
        mid-reception, sending a groomsman on an awkward liquor-store errand during cocktail hour. Over-order and
        you can easily waste thousands of dollars on bottles that sit unopened in someone's garage. The calculator
        above uses the same formulas caterers and event planners rely on&mdash;guest count, reception length, drinking
        percentage, and intensity&mdash;to give you a defensible, accurate number you can hand straight to your venue,
        caterer, or liquor retailer.
      </p>

      <H2 id="one-drink-rule">How to Calculate Alcohol for a Wedding (The Proven 1-Drink-Per-Hour Rule)</H2>
      <p className="leading-relaxed text-gray-700">
        The industry standard assumes each drinking guest consumes about one standard drink per hour, with adjustments
        for the crowd's overall energy. A "standard drink" is a 12oz beer, a 5oz glass of wine, or a 1.5oz shot of
        liquor&mdash;each contains roughly the same amount of pure alcohol. Multiply your guest count by the share who
        drink, then by hours of service, then by an intensity factor (1.0 for light drinkers, 1.25 for an average
        crowd, up to 1.75-2.0 for a heavy party crowd) to land on your total standard drinks needed. Cocktail hour and
        a longer reception both push this number up, while a brunch wedding or heavily food-forward event pulls it down.
      </p>

      <H2 id="bar-formula">The Ultimate Wedding Bar Formula & Breakdown Ratios</H2>
      <p className="leading-relaxed text-gray-700 mb-4">
        Once you know total drinks, split them by bar package. Each style shifts the wine/beer/spirits ratio:
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-[#F7EBE8]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Bar Style</th>
              <th className="text-left px-4 py-3 font-semibold">Wine</th>
              <th className="text-left px-4 py-3 font-semibold">Beer</th>
              <th className="text-left px-4 py-3 font-semibold">Spirits</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100">
              <td className="px-4 py-3 font-medium">Full Open Bar</td>
              <td className="px-4 py-3">50%</td>
              <td className="px-4 py-3">30%</td>
              <td className="px-4 py-3">20%</td>
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-4 py-3 font-medium">Beer & Wine Only</td>
              <td className="px-4 py-3">60%</td>
              <td className="px-4 py-3">40%</td>
              <td className="px-4 py-3">0%</td>
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-4 py-3 font-medium">Beer, Wine & Signature Cocktails</td>
              <td className="px-4 py-3">40%</td>
              <td className="px-4 py-3">30%</td>
              <td className="px-4 py-3">30%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <H2 id="average-cost">Average Cost of Liquor & Alcohol for a Wedding Reception</H2>
      <p className="leading-relaxed text-gray-700 mb-4">
        Most couples spend between $15 and $45 per guest on alcohol, depending on bar style and budget tier. A
        budget-friendly beer-and-wine bar sits near the low end, while a luxury open bar with top-shelf spirits and
        champagne pushes toward the high end. Here's a quick cost range benchmark by guest count:
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-[#F7EBE8]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Guest Count</th>
              <th className="text-left px-4 py-3 font-semibold">Estimated Cost Range</th>
            </tr>
          </thead>
          <tbody>
            {COST_TABLE.map(row => (
              <tr key={row.guests} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium">{row.guests} guests</td>
                <td className="px-4 py-3">${row.low.toLocaleString()} - ${row.high.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="quantity-table">How Much Wine, Beer & Liquor Do You Need? (Quick Reference Table)</H2>
      <p className="leading-relaxed text-gray-700 mb-4">
        These are approximate case counts for a Full Open Bar package at 85% drinking guests and average intensity,
        across 3, 4, and 5-hour receptions.
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-[#F7EBE8]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Guests</th>
              <th className="text-left px-4 py-3 font-semibold">3 hrs (Wine/Beer/Spirits cases)</th>
              <th className="text-left px-4 py-3 font-semibold">4 hrs</th>
              <th className="text-left px-4 py-3 font-semibold">5 hrs</th>
            </tr>
          </thead>
          <tbody>
            {QUANTITY_TABLE.map(row => (
              <tr key={row.guests} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium">{row.guests}</td>
                <td className="px-4 py-3">{row.h3.wine}/{row.h3.beer}/{row.h3.spirits}</td>
                <td className="px-4 py-3">{row.h4.wine}/{row.h4.beer}/{row.h4.spirits}</td>
                <td className="px-4 py-3">{row.h5.wine}/{row.h5.beer}/{row.h5.spirits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="pro-tips">How to Buy Alcohol for Your Wedding and Save Money (7 Pro Tips)</H2>
      <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
        {PRO_TIPS.map(tip => <li key={tip}>{tip}</li>)}
      </ol>

      <H2 id="mixers-ice">Mixers, Garnishes, and Ice: The Forgotten Wedding Bar Essentials</H2>
      <p className="leading-relaxed text-gray-700">
        Ice is the most commonly under-ordered item at wedding bars. Budget 1.5 lbs of ice per guest for a standard
        indoor reception, and bump that to 2 lbs per guest for a hot outdoor wedding. For garnishes, plan on roughly
        1 lime and 1 lemon per 10-15 cocktails, and stock at least 3 liters of mixers (soda, tonic, cranberry, lime
        juice) per bottle of spirits to keep cocktails balanced and non-alcoholic guests covered.
      </p>

      <H2 id="faq">Frequently Asked Questions</H2>
      <FaqAccordion />

      <H2 id="blog-ideas">50 Wedding Alcohol Planning Topics Worth Exploring</H2>
      <p className="leading-relaxed text-gray-700 mb-4">
        Curious about a more specific scenario? Here are 50 related topics our content library will expand into next.
      </p>
      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 list-decimal list-inside text-sm text-gray-600">
        {BLOG_IDEAS.map(idea => <li key={idea}>{idea}</li>)}
      </ol>
    </article>
  )
}
